/**
 * Batch SA-WIPE — super-admin tenant force-wipe dialog.
 *
 * Calls ``POST /admin/tenants/{id}/force-wipe`` with a typed-slug
 * confirmation + audit-trail reason. The action is **irreversible**: on
 * success the tenant is gone (DB + R2), the Stripe subscription is
 * canceled, and the operator is redirected to the tenants list.
 *
 * Design:   ``backend/docs/sa_force_wipe_design.md`` (frontend modal)
 * Security: ``backend/docs/sa_force_wipe_security.md`` (typed-slug
 * single field; tenant-name as second typed field deferred per CTO
 * Finding 3 ruling).
 *
 * Modal contract:
 *   * ``confirm_slug`` text input — disabled "Permanently delete"
 *     button until the trimmed value === tenant.slug (case-sensitive
 *     literal match).
 *   * ``reason`` textarea — required, 1–500 chars (mirrors backend
 *     Pydantic constraints; users see the friction client-side rather
 *     than as a 422).
 *   * Success → toast + redirect to /tenants (replace) + invalidate
 *     the tenant-list query.
 *   * Partial-R2 → warning-severity toast naming the remaining count
 *     and instructing the operator to re-run the CLI ``--r2-only``.
 *   * Failure → modal stays open; server error renders inline below
 *     the buttons; specific 400/409 error codes get human-friendly
 *     copy.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useForceWipeMutation,
  type TenantDetail,
} from "@/hooks/queries/admin/useAdminTenants";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  tenant: TenantDetail;
  onClose: () => void;
}

const MAX_REASON = 500;

function describeServerError(code: string | undefined): string | null {
  if (code === "slug_mismatch") {
    return "Slug mismatch — re-type the slug exactly.";
  }
  if (code === "wipe_in_progress") {
    return "A wipe is already in flight for this tenant — refresh in a moment.";
  }
  return null;
}

export function ForceWipeTenantDialog({ tenant, onClose }: Props): JSX.Element {
  const [confirmSlug, setConfirmSlug] = useState("");
  const [reason, setReason] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const wipe = useForceWipeMutation(tenant.id);
  const addToast = useToastStore((s) => s.addToast);
  const navigate = useNavigate();

  const slugMatches = confirmSlug.trim() === tenant.slug;
  const reasonTrimmed = reason.trim();
  const reasonOk = reasonTrimmed.length >= 1 && reasonTrimmed.length <= MAX_REASON;
  const canSubmit = slugMatches && reasonOk && !wipe.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);
    if (!slugMatches) {
      setErrMsg(`Type "${tenant.slug}" exactly to confirm.`);
      return;
    }
    if (!reasonOk) {
      setErrMsg(`Reason is required (1-${MAX_REASON} characters).`);
      return;
    }

    wipe.mutate(
      { confirm_slug: confirmSlug.trim(), reason: reasonTrimmed },
      {
        onSuccess: (data) => {
          if (data.r2_purge_status === "partial") {
            addToast(
              `Tenant "${tenant.name}" deleted. ` +
                `${data.r2_remaining_keys.length} R2 objects could not be ` +
                `purged — re-run the CLI's --r2-only flag.`,
              "warning",
            );
          } else {
            addToast(
              `Tenant "${tenant.name}" permanently deleted.`,
              "success",
            );
          }
          onClose();
          navigate("/tenants", { replace: true });
        },
        onError: (err) => {
          const serverCopy = describeServerError(err.error);
          if (serverCopy) {
            setErrMsg(serverCopy);
            return;
          }
          setErrMsg(
            err.message || "Could not delete tenant. Try again or check logs.",
          );
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-wipe-heading"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark"
      >
        <h2
          id="force-wipe-heading"
          className="text-base font-semibold text-red-700 dark:text-red-400"
        >
          Permanently delete tenant {tenant.name}?
        </h2>
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          This action is{" "}
          <strong className="text-red-700 dark:text-red-400">
            irreversible
          </strong>
          . All data (users, candidates, jobs, ratings, integrations, R2
          objects) will be wiped immediately and cannot be restored. The
          tenant&apos;s Stripe subscription will be canceled. There is no
          30-day grace period.
        </p>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Type{" "}
            <code className="rounded bg-surface-canvas px-1 py-0.5 text-xs text-text-primary dark:bg-white/[0.06] dark:text-text-primary">
              {tenant.slug}
            </code>{" "}
            to confirm
          </span>
          <input
            type="text"
            value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.target.value)}
            required
            autoComplete="off"
            spellCheck={false}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary"
            placeholder={tenant.slug}
            aria-label="Confirm tenant slug"
          />
        </label>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Reason (audit-trail, required, 1-{MAX_REASON} chars)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={1}
            maxLength={MAX_REASON}
            rows={3}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary"
            placeholder="e.g. ToS violation under triage; legal-erasure ticket-1234"
            aria-label="Reason for force-wipe"
          />
        </label>
        {errMsg ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errMsg}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
          >
            {wipe.isPending ? "Deleting…" : "Permanently delete"}
          </button>
        </div>
      </form>
    </div>
  );
}
